// src/utils/parseArticle.js
//
// Pure, synchronous parser for project long-descriptions.
//
// Supports a small markdown subset at the block level:
//   - Headings:  "# Title" → h2, "## Title" → h3, "### Title" → h4
//   - Lists:     consecutive lines starting with "-", "*" or "1." / "1)"
//   - Everything else is a paragraph.
// Inline formatting (bold, italic, code, links) is handled separately by
// utils/inlineMarkdown.js at render time.
//
// Image auto-placement: figures are woven into the prose based on how much
// text the reader has gone through. The target spacing between figures is
// totalWords / (imageCount + 1), so images spread evenly across whatever
// the author wrote. A figure is emitted at the first paragraph/list
// boundary that crosses the target — never directly after a heading.
// Images that don't fit inline fall through to the gallery at the bottom.
//
// Output: { blocks, unusedImageIndices }
//   blocks[i] = { type: 'paragraph', text }
//             | { type: 'heading', level, text }    // level ∈ 2..4
//             | { type: 'list', ordered, items }
//             | { type: 'figure', image }
//   unusedImageIndices = indices not placed inline, for the bottom gallery.
//
// The parser only decides WHERE figures land. How they look (side,
// numbering, sizing) is presentation and lives in ProjectArticle.

// Minimum words a block must contribute before we're willing to place an
// image after it. Without this, a one-line paragraph right after a figure
// would immediately trigger the next one. ~40 words ≈ two or three
// sentences of prose.
const MIN_WORDS_BETWEEN_FIGURES = 40;

const HEADING_RE = /^(#{1,3})\s+(.+)$/;
const BULLET_RE = /^[-*]\s+/;
const ORDERED_RE = /^\d+[.)]\s+/;

// Rough word-count helper. Splitting on whitespace is accurate enough for
// driving layout decisions — we're not indexing a search engine.
const countWords = (text) => (text.match(/\S+/g) || []).length;

// Legacy Firestore data sometimes stores literal "\\n\\n" instead of real
// newlines. Normalize both forms before parsing.
const normalize = (text) => text.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');

// Turn an image item (string or object) into the normalized shape the
// renderer expects.
const toImage = (item, index) => {
  if (!item) return null;
  if (typeof item === 'string') {
    return { src: item, thumbnailSrc: null, caption: null, index };
  }
  return {
    src: item.src,
    thumbnailSrc: item.thumbnailSrc || null,
    caption: item.caption || null,
    index,
  };
};

const lineKind = (line) => {
  if (BULLET_RE.test(line)) return 'ul';
  if (ORDERED_RE.test(line)) return 'ol';
  return 'p';
};

// Convert one blank-line-separated chunk into content blocks. A chunk is
// usually a single paragraph, but headings always stand alone, and runs of
// bullet lines become list blocks even without blank lines around them.
const chunkToBlocks = (chunk) => {
  const blocks = [];
  let run = [];
  let runKind = null;

  const endRun = () => {
    if (run.length === 0) return;
    if (runKind === 'p') {
      blocks.push({ type: 'paragraph', text: run.join(' ').trim() });
    } else {
      blocks.push({
        type: 'list',
        ordered: runKind === 'ol',
        items: run.map((l) => l.replace(BULLET_RE, '').replace(ORDERED_RE, '').trim()),
      });
    }
    run = [];
    runKind = null;
  };

  for (const rawLine of chunk.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const heading = line.match(HEADING_RE);
    if (heading) {
      endRun();
      // "#" maps to h2 — the page <h1> is the project title.
      blocks.push({
        type: 'heading',
        level: heading[1].length + 1,
        text: heading[2].trim(),
      });
      continue;
    }

    const kind = lineKind(line);
    if (kind !== runKind) endRun();
    runKind = kind;
    run.push(line);
  }
  endRun();

  return blocks;
};

const parseContentBlocks = (text) =>
  text
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .flatMap(chunkToBlocks);

// Words a block contributes to the reading-progress budget that drives
// figure placement.
const blockWords = (block) => {
  if (block.type === 'paragraph' || block.type === 'heading') {
    return countWords(block.text);
  }
  if (block.type === 'list') {
    return block.items.reduce((sum, item) => sum + countWords(item), 0);
  }
  return 0;
};

// Figures may follow prose, never a heading — a section title should hug
// the text it introduces.
const canPlaceAfter = (block) => block.type === 'paragraph' || block.type === 'list';

/**
 * Parse a long description + image array into a flat list of render blocks.
 *
 * @param {string} description  - long description text (markdown subset)
 * @param {Array}  images       - additionalImages from Firestore
 * @returns {{ blocks: Array, unusedImageIndices: number[] }}
 */
export function parseArticle(description, images = []) {
  const normalizedImages = images.map(toImage);
  const allImageIndices = normalizedImages.map((_, i) => i);

  if (!description) {
    return { blocks: [], unusedImageIndices: allImageIndices };
  }

  const contentBlocks = parseContentBlocks(normalize(description));

  if (contentBlocks.length === 0 || normalizedImages.length === 0) {
    return { blocks: contentBlocks, unusedImageIndices: allImageIndices };
  }

  const wordCounts = contentBlocks.map(blockWords);
  const totalWords = wordCounts.reduce((a, b) => a + b, 0);
  const imageCount = normalizedImages.length;

  // Target spacing: words between figures. Dividing by (imageCount + 1)
  // leaves room for text before the first image and after the last one.
  // The minimum floor keeps short paragraphs from triggering figures.
  const targetSpacing = Math.max(
    MIN_WORDS_BETWEEN_FIGURES,
    Math.floor(totalWords / (imageCount + 1))
  );

  const blocks = [];
  const usedIndices = new Set();
  let imageCursor = 0;
  let wordsSinceLastFigure = 0;
  // Tracked so we never emit a figure when there isn't enough prose left
  // to close the article on text.
  let wordsRemaining = totalWords;

  for (let i = 0; i < contentBlocks.length; i++) {
    const block = contentBlocks[i];
    blocks.push(block);
    wordsSinceLastFigure += wordCounts[i];
    wordsRemaining -= wordCounts[i];

    // Drop an image in at this boundary if:
    //   - we still have images left, AND
    //   - this block can host a figure after it (prose, not a heading), AND
    //   - enough text has passed since the last figure, AND
    //   - there's still enough text ahead to end the article on prose.
    const hasEnoughTextAhead = wordsRemaining >= MIN_WORDS_BETWEEN_FIGURES;
    const hasEnoughTextBehind = wordsSinceLastFigure >= targetSpacing;
    const imagesLeft = imageCursor < imageCount;

    if (imagesLeft && canPlaceAfter(block) && hasEnoughTextBehind && hasEnoughTextAhead) {
      const image = normalizedImages[imageCursor];
      blocks.push({ type: 'figure', image });
      usedIndices.add(image.index);
      imageCursor++;
      wordsSinceLastFigure = 0;
    }
  }

  return {
    blocks,
    unusedImageIndices: allImageIndices.filter((i) => !usedIndices.has(i)),
  };
}
